import React, { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L, { LatLngTuple } from 'leaflet';
import { useMapInstance } from './MapContext';
import { EntTerritorialsUnitsPoligons } from '../api/territorialsUnitsPoligons';
import { ResponseDataGet } from '../../models/responseDataGet';

interface ExistingMapProps {
  polygonsData?: ResponseDataGet<EntTerritorialsUnitsPoligons>;
  polygonsLoading?: boolean;
  extraData?: any[];
  customIcon?: L.Icon;
  onPolygonClick?: (regionId: number) => void;
  selectedRegionId?: number | null;
  showAllDistricts?: boolean;
}

export const ExistingMap: React.FC<ExistingMapProps> = ({ 
  polygonsData, 
  polygonsLoading,
  extraData,
  customIcon,
  onPolygonClick,
  selectedRegionId,
  showAllDistricts
}) => {
  const map = useMap();
  const { setMap } = useMapInstance();
  const polygonLayersRef = React.useRef<{ [key: string]: L.Polygon }>({});
  const markersRef = React.useRef<L.Marker[]>([]);

  
  useEffect(() => {
    if (map) {
      setMap(map);
      return () => setMap(null);
    }
  }, [map, setMap]);

 
  useEffect(() => {
    if (!map || !extraData) return;

   
    const markerPane = map.createPane('markerPane');
    const tooltipPane = map.createPane('tooltipPane');
    const regionPane = map.createPane('regionPane');
    const areaPane = map.createPane('areaPane');
    const cityPane = map.createPane('cityPane');
    
    if (markerPane && tooltipPane && regionPane && areaPane && cityPane) {
      tooltipPane.style.zIndex = '800'; 
      markerPane.style.zIndex = '700';  
      regionPane.style.zIndex = '650';   
      areaPane.style.zIndex = '600';     
      cityPane.style.zIndex = '550';     
    }

    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    extraData.forEach(item => {
      if (item.latitude && item.longitude) {
        const marker = L.marker([item.latitude, item.longitude], {
          icon: customIcon || L.Marker.prototype.options.icon,
          pane: 'markerPane' 
        })
          .addTo(map)
          .bindPopup(item.popupContent || `
            <div>
              <h3>${item.name || 'Без названия'}</h3>
              ${item.address ? `<p>Адрес: ${item.address}</p>` : ''}
              ${item.type ? `<p>Тип: ${item.type}</p>` : ''}
            </div>
          `, {
            pane: 'tooltipPane' 
          });
        markersRef.current.push(marker);
      }
    });

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    };
  }, [map, extraData, customIcon]);

 
  useEffect(() => {
    if (!map || polygonsLoading) return;

    const clearPolygons = () => {
      Object.values(polygonLayersRef.current).forEach(layer => {
        layer.remove();
      });
      polygonLayersRef.current = {};
    };

    clearPolygons();

    if (!polygonsData?.res) {
      console.warn('No polygons data available');
      return clearPolygons;
    }

    const polygonsArray = Array.isArray(polygonsData.res) ? polygonsData.res : [];
    if (polygonsArray.length === 0) {
      console.warn('No polygons in data');
      return clearPolygons;
    }

    const regionPane = map.createPane('regionPane');
    const areaPane = map.createPane('areaPane');
    const cityPane = map.createPane('cityPane');
    
    if (regionPane && areaPane && cityPane) {
      regionPane.style.zIndex = '650';
      areaPane.style.zIndex = '600';
      cityPane.style.zIndex = '550';
    }

    const processCoordinates = (coords: number[][][]): LatLngTuple[][] => {
      return coords.map(ring => 
        ring.map(coord => [coord[1], coord[0]] as LatLngTuple)
      );
    };

    const getPolygonStyle = (type: string): L.PolylineOptions & { pane: string; fillOpacity: number; weight: number } => {
      switch(type) {
        case 'REGION':
          return {
            fillOpacity: 0.2,
            weight: 2,
            pane: 'regionPane'
          };
        case 'AREA':
          return {
            fillOpacity: 0.1,
            weight: 1,
            pane: 'areaPane'
          };
        default:
          return {
            fillOpacity: 0.05,
            weight: 1,
            pane: 'cityPane'
          };
      }
    };

    const filteredPolygons = polygonsArray.filter((polygon: EntTerritorialsUnitsPoligons) => 
      polygon.type === 'REGION' || polygon.type === 'AREA' || polygon.type === 'CITY_OF_REPUBLICAN_SIGNIFICANCE'
    ).sort((a: EntTerritorialsUnitsPoligons, b: EntTerritorialsUnitsPoligons) => {
      if (a.type === 'REGION') return 1;
      if (b.type === 'REGION') return -1;
      if (a.type === 'AREA') return 1;
      if (b.type === 'AREA') return -1;
      return 0;
    });

    console.log('Filtered polygons:', {
      total: filteredPolygons.length,
      regions: filteredPolygons.filter(p => p.type === 'REGION').length,
      areas: filteredPolygons.filter(p => p.type === 'AREA').length,
      cities: filteredPolygons.filter(p => p.type === 'CITY_OF_REPUBLICAN_SIGNIFICANCE').length,
      hasRegion2067: filteredPolygons.some(p => p.id === 2067)
    });

    filteredPolygons.forEach((polygon: EntTerritorialsUnitsPoligons) => {
      try {
        if (!polygon.polygonJson) {
          console.warn('No polygonJson for polygon:', polygon);
          return;
        }

        const parsedPolygon = JSON.parse(polygon.polygonJson);
        if (!parsedPolygon?.[0]?.geojson) {
          console.error(`Invalid polygon structure for ${polygon.nameRu}`);
          return;
        }

        let coordinates: LatLngTuple[][] = [];
        const geojson = parsedPolygon[0].geojson;

        if (geojson.type === 'MultiPolygon') {
          coordinates = geojson.coordinates.flatMap((polygonCoords: number[][][]) => 
            processCoordinates(polygonCoords)
          );
        } else if (geojson.type === 'Polygon') {
          coordinates = processCoordinates(geojson.coordinates);
        }

        if (coordinates.length === 0) {
          console.warn(`No valid coordinates for polygon ${polygon.nameRu}`);
          return;
        }

        // Отладочная информация для полигона 2067
        if (polygon.id === 2067) {
          console.log('Polygon 2067 coordinates:', {
            coordinatesLength: coordinates.length,
            firstCoordinate: coordinates[0],
            polygonName: polygon.nameRu
          });
        }

        const style = getPolygonStyle(polygon.type);
        
        const regionData = extraData?.find((item: any) => item.regionId === polygon.id);
        const fillColor = regionData?.color || 'rgba(255, 0, 0, 0.1)';
        
        if (polygon.id === 2067) {
          console.log('Processing polygon 2067:', {
            polygon,
            regionData,
            fillColor,
            extraDataLength: extraData?.length,
            hasPolygonJson: !!polygon.polygonJson
          });
        }
        
        // Определяем прозрачность в зависимости от типа полигона и выбранной области
        let fillOpacity = 0.8;
        if (polygon.type === 'REGION' && selectedRegionId) {
          // Если это область и выбрана область, делаем её прозрачной
          fillOpacity = 0;
        } else if (polygon.type === 'REGION' && showAllDistricts) {
          // Если это область и включен режим показа всех районов, делаем её прозрачной
          fillOpacity = 0;
        } else if (polygon.type === 'REGION') {
          // Если это область и обычный режим, показываем полностью закрашенной
          fillOpacity = 1;
        } else {
          // Для районов и городов всегда непрозрачные
          fillOpacity = 1;
        }



        const polygonOptions: L.PolylineOptions = {
          ...style,
          color: 'rgb(255, 255, 255)', // белая граница
          fillColor: fillColor,
          fillOpacity: fillOpacity,
          weight: 2, // более узкая граница
          smoothFactor: 1
        };

        const polygonLayer = L.polygon(coordinates, polygonOptions)
          .addTo(map)
          .bindPopup(`
            <div>
              <h3>${polygon.nameRu}</h3>
              ${regionData ? `<p>Количество животных: ${regionData.amount?.toLocaleString() || 0}</p>` : ''}
              <p>Тип: ${polygon.type}</p>
            </div>
          `, {
            pane: 'tooltipPane'
          });

        // Добавляем обработчик клика
        if (onPolygonClick && polygon.id != null) {
          polygonLayer.on('click', () => {
            onPolygonClick(polygon.id);
          });
        }

        if (polygon.id != null) {
          polygonLayersRef.current[polygon.id.toString()] = polygonLayer;
        }
      } catch (error) {
        console.error(`Error processing polygon for ${polygon.nameRu}:`, error);
      }
    });


    const allLayers = [
      ...Object.values(polygonLayersRef.current),
      ...markersRef.current
    ];
    
    if (allLayers.length > 0) {
      const group = L.featureGroup(allLayers);
      map.fitBounds(group.getBounds(), { padding: [50, 50] });
    }

    return clearPolygons;
  }, [map, polygonsData, polygonsLoading, onPolygonClick]);

  return null;
};